/**
 * AdminMarkenSection — Marken-Konfiguration (nur Admin)
 * 
 * Zeigt alle Marken (Gutachter Koop, Unfallgiganten, Unfallwiki, Protex)
 * mit Design-Einstellungen, KI-Anweisungen und Domain-Konfiguration.
 */

import { useState } from 'react';
import {
  Palette, Globe, Cpu, Save, Edit2, X, ChevronDown, ChevronRight,
  BookOpen, Layers, ExternalLink, Check,
} from 'lucide-react';
import { useMarkenConfig, type MarkenConfig } from '@/hooks/useAdminSettings';
import { toast } from 'sonner';
import BrandRegenBatchCard from './BrandRegenBatchCard';

function ColorPreview({ color, label }: { color: string | null; label: string }) {
  if (!color) return null;
  return (
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 rounded border border-gray-200" style={{ backgroundColor: color }} />
      <span className="text-[10px] font-mono text-gray-500">{color}</span>
      <span className="text-[10px] text-gray-400">{label}</span>
    </div>
  );
}

export default function AdminMarkenSection() {
  const { data, loading, update } = useMarkenConfig();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<MarkenConfig>>({});
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleStartEdit = (marke: MarkenConfig) => {
    setEditingId(marke.id);
    setEditData({
      anzeigename: marke.anzeigename,
      beschreibung: marke.beschreibung || '',
      farbe_primaer: marke.farbe_primaer || '',
      farbe_sekundaer: marke.farbe_sekundaer || '',
      domain: marke.domain || '',
      ki_anweisungen: marke.ki_anweisungen || '',
      design_richtlinien: marke.design_richtlinien || '',
      logo_url: marke.logo_url || '',
    });
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    const success = await update(editingId, editData);
    setSaving(false);
    if (success) {
      setEditingId(null);
      setEditData({});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-[#9B1C1C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
            <Layers className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Marken-Konfiguration</h2>
            <p className="text-xs text-gray-400">Design, KI-Anweisungen und Domain-Einstellungen pro Marke</p>
          </div>
        </div>
      </div>

      <BrandRegenBatchCard />

      {/* Marken-Karten */}
      {data.map((marke) => {
        const isExpanded = expandedId === marke.id;
        const isEditing = editingId === marke.id;

        return (
          <div key={marke.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Marken-Header mit Farbbalken */}
            <div
              className="h-1.5"
              style={{
                background: marke.farbe_primaer && marke.farbe_sekundaer
                  ? `linear-gradient(90deg, ${marke.farbe_primaer}, ${marke.farbe_sekundaer})`
                  : '#E5E7EB',
              }}
            />

            <button
              onClick={() => setExpandedId(isExpanded ? null : marke.id)}
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
            >
              {/* Logo / Farbkreis */}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                style={{ backgroundColor: marke.farbe_primaer || '#6B7280' }}
              >
                {marke.anzeigename.substring(0, 2).toUpperCase()}
              </div>

              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-800">{marke.anzeigename}</p>
                  {marke.ist_aktiv && (
                    <span className="text-[9px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">aktiv</span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400">{marke.beschreibung || 'Keine Beschreibung'}</p>
                {marke.domain && (
                  <p className="text-[10px] text-blue-500 mt-0.5">{marke.domain}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {marke.ki_anweisungen && (
                  <span className="text-[9px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Cpu className="w-3 h-3" /> KI
                  </span>
                )}
                {marke.design_richtlinien && (
                  <span className="text-[9px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Palette className="w-3 h-3" /> Design
                  </span>
                )}
                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && !isEditing && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-4">
                {/* Design-Info */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Design</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ColorPreview color={marke.farbe_primaer} label="Primär" />
                    <ColorPreview color={marke.farbe_sekundaer} label="Sekundär" />
                  </div>
                  {marke.design_richtlinien && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{marke.design_richtlinien}</p>
                    </div>
                  )}
                </div>

                {/* KI-Anweisungen */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">KI-Anweisungen</p>
                  {marke.ki_anweisungen ? (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{marke.ki_anweisungen}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-300 italic">Keine KI-Anweisungen hinterlegt</p>
                  )}
                </div>

                {/* Domain */}
                {marke.domain && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Domain</p>
                    <a
                      href={`https://${marke.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      {marke.domain}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Bearbeiten-Button */}
                <button
                  onClick={() => handleStartEdit(marke)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Bearbeiten
                </button>
              </div>
            )}

            {/* Edit Mode */}
            {isExpanded && isEditing && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Anzeigename</label>
                    <input
                      value={editData.anzeigename || ''}
                      onChange={e => setEditData(p => ({ ...p, anzeigename: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Domain</label>
                    <input
                      value={editData.domain || ''}
                      onChange={e => setEditData(p => ({ ...p, domain: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      placeholder="z.B. unfallgiganten.de"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-medium text-gray-500">Beschreibung</label>
                  <textarea
                    value={editData.beschreibung || ''}
                    onChange={e => setEditData(p => ({ ...p, beschreibung: e.target.value }))}
                    rows={2}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Primärfarbe</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={editData.farbe_primaer || '#000000'}
                        onChange={e => setEditData(p => ({ ...p, farbe_primaer: e.target.value }))}
                        className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                      />
                      <input
                        value={editData.farbe_primaer || ''}
                        onChange={e => setEditData(p => ({ ...p, farbe_primaer: e.target.value }))}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                        placeholder="#9B1C1C"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Sekundärfarbe</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={editData.farbe_sekundaer || '#000000'}
                        onChange={e => setEditData(p => ({ ...p, farbe_sekundaer: e.target.value }))}
                        className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                      />
                      <input
                        value={editData.farbe_sekundaer || ''}
                        onChange={e => setEditData(p => ({ ...p, farbe_sekundaer: e.target.value }))}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                        placeholder="#C41E3A"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-medium text-gray-500">Logo-URL</label>
                  <input
                    value={editData.logo_url || ''}
                    onChange={e => setEditData(p => ({ ...p, logo_url: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="text-[10px] font-medium text-gray-500">Design-Richtlinien</label>
                  <textarea
                    value={editData.design_richtlinien || ''}
                    onChange={e => setEditData(p => ({ ...p, design_richtlinien: e.target.value }))}
                    rows={4}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
                    placeholder="Schriftarten, Abstände, Bildsprache, Tonalität..."
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Cpu className="w-3.5 h-3.5 text-amber-600" />
                    <label className="text-[10px] font-medium text-gray-500">KI-Anweisungen</label>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-1">
                    Automatisierungs-Anweisungen für Aufgaben dieser Marke.
                  </p>
                  <textarea
                    value={editData.ki_anweisungen || ''}
                    onChange={e => setEditData(p => ({ ...p, ki_anweisungen: e.target.value }))}
                    rows={6}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none font-mono"
                    placeholder="Du bist der KI-Assistent für die Marke [Name]. Deine Aufgaben:&#10;1. ...&#10;2. ...&#10;&#10;Stil: ...&#10;Tonalität: ..."
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => { setEditingId(null); setEditData({}); }}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Speichern
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {data.length === 0 && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center shadow-sm">
          <Layers className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-400">Keine Marken konfiguriert</p>
        </div>
      )}
    </div>
  );
}
